/**
 * Lazy Video Loader with Intersection Observer
 * Optimizes video loading by only loading and playing videos when they're in viewport
 */

class VideoLazyLoader {
  constructor() {
    this.videos = document.querySelectorAll('video[data-lazy-video]');
    this.iframes = document.querySelectorAll('iframe[data-lazy-video]');
    this.allMedia = [...this.videos, ...this.iframes];
    this.isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    this.visibleVideos = new Set(); // Track videos currently in viewport
    this.observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: this.isMobile ? 0.1 : 0.25 // Lower threshold on mobile
    };

    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        this.handleIntersection.bind(this),
        this.observerOptions
      );

      this.allMedia.forEach(media => {
        this.observer.observe(media);

        if (media.tagName === 'VIDEO') {
          // Mobile: ensure required attributes for autoplay
          if (this.isMobile) {
            media.setAttribute('webkit-playsinline', '');
            media.muted = true; // Property must be set, not just attribute
          }

          // Add loading event listeners for video
          media.addEventListener('loadeddata', () => {
            media.style.opacity = '1';
            media.parentElement?.classList.remove('video-loading');
            media.parentElement?.classList.add('video-loaded');
          });

          media.addEventListener('error', () => {
            const retryCount = parseInt(media.dataset.retryCount || '0');
            const maxRetries = 3;
            
            if (retryCount < maxRetries) {
              // Exponential backoff: 1s, 2s, 4s
              const delay = Math.pow(2, retryCount) * 1000;
              
              setTimeout(() => {
                media.dataset.retryCount = String(retryCount + 1);
                // Reload video sources
                const sources = media.querySelectorAll('source');
                if (sources.length > 0) {
                  sources.forEach(source => {
                    if (source.dataset.src) {
                      source.src = source.dataset.src;
                    }
                  });
                } else if (media.dataset.src) {
                  media.src = media.dataset.src;
                }
                media.load();
                this.playVideo(media);
              }, delay);
            }
            // No error class added - poster remains visible as graceful fallback
          });

          // Mobile only: re-play videos paused by external code (e.g., pauseAllMedia)
          if (this.isMobile) {
            media.addEventListener('pause', () => {
              // If video is in viewport and was loaded, re-play after short delay
              if (this.visibleVideos.has(media) && media.dataset.loaded) {
                setTimeout(() => {
                  if (this.visibleVideos.has(media) && media.paused) {
                    this.playVideo(media);
                  }
                }, 100);
              }
            });
          }
        } else if (media.tagName === 'IFRAME') {
          // For iframes, show immediately when loaded
          media.addEventListener('load', () => {
            media.style.opacity = '1';
            media.parentElement?.classList.remove('video-loading');
            media.parentElement?.classList.add('video-loaded');
          });
        }
      });
    } else {
      // Fallback for browsers without IntersectionObserver
      this.loadAllMedia();
    }
  }

  handleIntersection(entries) {
    entries.forEach(entry => {
      const media = entry.target;

      if (entry.isIntersecting) {
        // Track visible videos (for mobile re-play logic)
        if (media.tagName === 'VIDEO') {
          this.visibleVideos.add(media);
        }
        // Media is in viewport
        this.loadMedia(media);
      } else {
        // Remove from visible tracking
        if (media.tagName === 'VIDEO') {
          this.visibleVideos.delete(media);
        }
        // Media is out of viewport - pause video to save resources
        if (media.tagName === 'VIDEO' && !media.paused) {
          media.pause();
        }
      }
    });
  }

  loadMedia(media) {
    // Load media if not already loaded
    if (!media.dataset.loaded) {
      if (media.tagName === 'VIDEO') {
        const sources = media.querySelectorAll('source');

        if (sources.length > 0) {
          // Load all source elements
          sources.forEach(source => {
            if (source.dataset.src) {
              source.src = source.dataset.src;
            }
          });
          media.load();
          media.dataset.loaded = 'true';
        } else if (media.dataset.src) {
          // Fallback if video src is directly on video element
          media.src = media.dataset.src;
          media.load();
          media.dataset.loaded = 'true';
        }

        // Play video with error handling
        this.playVideo(media);
      } else if (media.tagName === 'IFRAME') {
        // Load iframe
        if (media.dataset.src) {
          media.src = media.dataset.src;
          media.dataset.loaded = 'true';
        }
      }
    } else if (media.tagName === 'VIDEO') {
      // If already loaded, just play
      this.playVideo(media);
    }
  }

  playVideo(video) {
    // Mobile: ensure muted before play (required for autoplay)
    if (this.isMobile) {
      video.muted = true;
    }

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(error => {
        // Auto-play was prevented - this is normal on mobile without interaction
        if (this.isMobile) {
          console.log('Mobile autoplay blocked - will play on user interaction');
        }
      });
    }
  }

  loadAllMedia() {
    // Fallback: load all media immediately
    this.allMedia.forEach(media => this.loadMedia(media));
  }
}

// Global instance to prevent multiple initializations
let videoLazyLoaderInstance = null;

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    videoLazyLoaderInstance = new VideoLazyLoader();
  });
} else {
  videoLazyLoaderInstance = new VideoLazyLoader();
}

// Function to reinitialize video lazy loader (called after filter changes, etc.)
function reinitializeVideoLazyLoader() {
  // Cleanup old instance if it exists
  if (videoLazyLoaderInstance && videoLazyLoaderInstance.observer) {
    videoLazyLoaderInstance.observer.disconnect();
  }
  videoLazyLoaderInstance = new VideoLazyLoader();
}

// Expose globally for facets.js to call after filter changes
window.reinitializeVideoLazyLoader = reinitializeVideoLazyLoader;

// Re-initialize after AJAX/dynamic content loads (for quick-add, filters, etc.)
document.addEventListener('shopify:section:load', () => {
  reinitializeVideoLazyLoader();
});

// Handle non-lazy product videos (direct autoplay videos)
// These need protection from pauseAllMedia() too
(function() {
  function setupProductVideos() {
    document.querySelectorAll('video[autoplay]:not([data-lazy-video])').forEach(video => {
      if (video.dataset.autoplayProtected) return;
      video.dataset.autoplayProtected = 'true';

      // Re-play if paused by external code
      video.addEventListener('pause', () => {
        setTimeout(() => {
          if (video.paused && document.contains(video)) {
            video.muted = true;
            video.play().catch(() => {});
          }
        }, 50);
      });

      // Ensure it plays when ready
      if (video.readyState >= 3) {
        video.muted = true;
        video.play().catch(() => {});
      } else {
        video.addEventListener('canplay', () => {
          video.muted = true;
          video.play().catch(() => {});
        }, { once: true });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupProductVideos);
  } else {
    setupProductVideos();
  }

  document.addEventListener('shopify:section:load', setupProductVideos);
})();
